import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type {
  AgriCampaign, AgriCampaignCharge, AgriSubsidy,
  AgriLivestockHerd, AgriLivestockSale, AgriLivestockCharge, AgriEquipment
} from '../utils/agriEngine';

export function useAgriCampaigns(year: number) {
  const [campaigns, setCampaigns] = useState<AgriCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agri_campaigns')
      .select('*')
      .eq('campaign_year', year)
      .order('created_at', { ascending: false });
    setCampaigns(data || []);
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);
  return { campaigns, loading, refresh: load };
}

export function useAgriCampaignCharges(campaignId: string | null) {
  const [charges, setCharges] = useState<AgriCampaignCharge[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) { setCharges([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('agri_campaign_charges')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('charge_date', { ascending: true, nullsFirst: false });
    setCharges(data || []);
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);
  return { charges, loading, refresh: load };
}

export function useAllAgriCharges(year: number) {
  const [chargesByCampaign, setChargesByCampaign] = useState<Record<string, AgriCampaignCharge[]>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: campaigns } = await supabase
      .from('agri_campaigns')
      .select('id')
      .eq('campaign_year', year);

    if (!campaigns || campaigns.length === 0) {
      setChargesByCampaign({});
      setLoading(false);
      return;
    }
    const ids = campaigns.map(c => c.id);
    const { data: charges } = await supabase
      .from('agri_campaign_charges')
      .select('*')
      .in('campaign_id', ids);

    const map: Record<string, AgriCampaignCharge[]> = {};
    for (const charge of charges || []) {
      if (!map[charge.campaign_id]) map[charge.campaign_id] = [];
      map[charge.campaign_id].push(charge);
    }
    setChargesByCampaign(map);
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);
  return { chargesByCampaign, loading, refresh: load };
}

export function useAgriSubsidies(year: number) {
  const [subsidies, setSubsidies] = useState<AgriSubsidy[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agri_subsidies')
      .select('*')
      .eq('campaign_year', year)
      .order('theoretical_payment_date', { ascending: true, nullsFirst: false });
    setSubsidies(data || []);
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);
  return { subsidies, loading, refresh: load };
}

export function useAgriLivestockHerds() {
  const [herds, setHerds] = useState<AgriLivestockHerd[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agri_livestock_herds')
      .select('*')
      .order('created_at', { ascending: false });
    setHerds(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  return { herds, loading, refresh: load };
}

export function useAgriLivestockSales(herdId: string | null, year: number) {
  const [sales, setSales] = useState<AgriLivestockSale[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!herdId) { setSales([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('agri_livestock_sales')
      .select('*')
      .eq('herd_id', herdId)
      .eq('campaign_year', year)
      .order('planned_date', { ascending: true, nullsFirst: false });
    setSales(data || []);
    setLoading(false);
  }, [herdId, year]);

  useEffect(() => { load(); }, [load]);
  return { sales, loading, refresh: load };
}

export function useAgriLivestockCharges(herdId: string | null, year: number) {
  const [charges, setCharges] = useState<AgriLivestockCharge[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!herdId) { setCharges([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('agri_livestock_charges')
      .select('*')
      .eq('herd_id', herdId)
      .eq('campaign_year', year)
      .order('created_at');
    setCharges(data || []);
    setLoading(false);
  }, [herdId, year]);

  useEffect(() => { load(); }, [load]);
  return { charges, loading, refresh: load };
}

export function useAgriEquipment() {
  const [equipment, setEquipment] = useState<AgriEquipment[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agri_equipment')
      .select('*')
      .order('created_at', { ascending: false });
    setEquipment(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  return { equipment, loading, refresh: load };
}
